import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { wireQuotationsAPI, customersAPI } from '../../services/api';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import { Plus, Search, FileText, Eye, Loader2, Cable } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'JPY'];

const fmt = (n, ccy = 'INR', dec = 2) => {
  const sym = ccy === 'INR' ? '₹' : ccy === 'USD' ? '$' : ccy === 'EUR' ? '€' : `${ccy} `;
  return `${sym}${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
};
const fmtDate = (s) => { if (!s) return '-'; try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; } };
const statusColor = (s) => {
  const x = String(s || '').toLowerCase();
  if (['approved', 'won', 'released'].includes(x)) return 'bg-emerald-100 text-emerald-700';
  if (['rejected', 'lost'].includes(x)) return 'bg-red-100 text-red-700';
  if (['draft', 'pending'].includes(x)) return 'bg-slate-100 text-slate-700';
  return 'bg-blue-100 text-blue-700';
};

function NewWireQuotationForm({ onCreated, onCancel }) {
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [form, setForm] = useState({
    customer_id: '',
    customer_name: '',
    customer_currency: 'INR',
    currency: 'INR',
    exchange_rate: 1,
    validity_days: 30,
    delivery_terms: '',
    payment_terms: '',
    incoterms: '',
    contact_person: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    customersAPI.list().then((r) => setCustomers(r.data.customers || r.data || []));
  }, []);

  const filteredCustomers = customers.filter((c) =>
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.customer_code?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const pickCustomer = (c) => {
    setForm((f) => ({
      ...f,
      customer_id: c.id,
      customer_name: c.name,
      customer_currency: c.currency || 'INR',
      currency: c.currency || 'INR',
      payment_terms: c.payment_terms || '',
    }));
    setCustomerSearch('');
  };

  const handleSubmit = async () => {
    if (!form.customer_id) { toast.error('Select a customer'); return; }
    setSaving(true);
    try {
      const res = await wireQuotationsAPI.create({
        customer_id: form.customer_id,
        currency: form.currency,
        exchange_rate: parseFloat(form.exchange_rate) || 1,
        validity_days: parseInt(form.validity_days) || 30,
        delivery_terms: form.delivery_terms || null,
        payment_terms: form.payment_terms || null,
        incoterms: form.incoterms || null,
        contact_person: form.contact_person || null,
        notes: form.notes || null,
      });
      toast.success(`Wire quotation ${res.data.quotation_number} created`);
      if (onCreated) onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create wire quotation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="new-wire-quotation-form">
      <div className="space-y-1.5">
        <Label>Customer *</Label>
        {form.customer_id ? (
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded border">
            <span className="text-sm font-medium">{form.customer_name}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, customer_id: '', customer_name: '' }))}>×</Button>
          </div>
        ) : (
          <div className="relative">
            <Input value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder="Search customer…" data-testid="wq-customer-search" />
            {customerSearch && (
              <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredCustomers.slice(0, 10).map((c) => (
                  <div key={c.id} className="p-2 hover:bg-muted cursor-pointer" onClick={() => pickCustomer(c)} data-testid={`wq-pick-customer-${c.id}`}>
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.customer_code} · {c.currency || 'INR'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Currency</Label>
          <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
            <SelectTrigger data-testid="wq-currency"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Exchange Rate (vs INR)</Label>
          <Input type="number" step="0.01" value={form.exchange_rate} onChange={(e) => setForm((f) => ({ ...f, exchange_rate: e.target.value }))} disabled={form.currency === 'INR'} />
        </div>
        <div>
          <Label>Validity (days)</Label>
          <Input type="number" value={form.validity_days} onChange={(e) => setForm((f) => ({ ...f, validity_days: e.target.value }))} />
        </div>
        <div className="col-span-3">
          <Label>Contact Person</Label>
          <Input value={form.contact_person} onChange={(e) => setForm((f) => ({ ...f, contact_person: e.target.value }))} placeholder="optional" />
        </div>
        <div>
          <Label>Payment Terms</Label>
          <Input value={form.payment_terms} onChange={(e) => setForm((f) => ({ ...f, payment_terms: e.target.value }))} placeholder="e.g., 30 days net" />
        </div>
        <div>
          <Label>Delivery Terms</Label>
          <Input value={form.delivery_terms} onChange={(e) => setForm((f) => ({ ...f, delivery_terms: e.target.value }))} placeholder="optional" />
        </div>
        <div>
          <Label>Incoterms</Label>
          <Input value={form.incoterms} onChange={(e) => setForm((f) => ({ ...f, incoterms: e.target.value }))} placeholder="e.g., FOB Nhava Sheva" />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-3 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="button" className="btn-primary" onClick={handleSubmit} disabled={saving || !form.customer_id} data-testid="wq-create-btn">
          {saving ? 'Creating…' : 'Create Wire Quotation'}
        </Button>
      </div>
    </div>
  );
}

export default function WireQuotationsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => {
    setLoading(true);
    wireQuotationsAPI.list({ search: search || undefined })
      .then((r) => setRows(r.data.items || []))
      .catch(() => toast.error('Failed to load wire quotations'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const handleCreated = (q) => {
    setDialogOpen(false);
    navigate(`/wire/quotations/${q.id}`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wire-quotations-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight flex items-center gap-3">
            <FileText className="h-7 w-7 text-blue-600" />
            Wire Quotations
            <Cable className="h-5 w-5 text-blue-400 opacity-70" />
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Per-kg / per-ton quotations for wire products</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-wire-quotation-btn"><Plus className="h-4 w-4 mr-2" />New Wire Quotation</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Wire Quotation</DialogTitle>
              <DialogDescription>Choose customer and commercial terms. You'll add line items on the next screen.</DialogDescription>
            </DialogHeader>
            <NewWireQuotationForm onCreated={handleCreated} onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search WQ#, customer, grade, part…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="wq-search" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>WQ Number</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((q) => (
                  <TableRow key={q.id} className="hover:bg-muted/40" data-testid={`wq-row-${q.id}`}>
                    <TableCell className="font-mono font-medium">{q.quotation_number}{q.version_number > 1 && <span className="text-muted-foreground ml-1">v{q.version_number}</span>}</TableCell>
                    <TableCell>{q.customer_name}</TableCell>
                    <TableCell className="text-right font-mono">{(q.items || []).length}</TableCell>
                    <TableCell>{q.currency}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(q.total_amount || q.subtotal, q.currency)}</TableCell>
                    <TableCell><Badge className={statusColor(q.status)} variant="outline">{q.status}</Badge></TableCell>
                    <TableCell className="text-xs">{fmtDate(q.created_at)}</TableCell>
                    <TableCell><Button variant="ghost" size="sm" onClick={() => navigate(`/wire/quotations/${q.id}`)}><Eye className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No wire quotations yet. Click <strong>New Wire Quotation</strong> to start.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
